import type { CalendarEventDto } from '@chat-emp/shared';

import { addDays, startOfDay } from './calendar-rules.js';

/**
 * What an event is, in time: which day it belongs to, where it sits down a
 * column, and what happens when two of them want the same hour.
 *
 * Separate from `calendar-rules`, which knows only about days and grids and has
 * never heard of an event. This is the half that knows what is *in* the grid.
 */

const MS_PER_MINUTE = 60_000;

/**
 * A span of time, which is all the placing functions need.
 *
 * Narrower than `CalendarEventDto` on purpose: the same maths places a whole
 * event and a bare pair of timestamps.
 */
export interface TimeSpan {
  startsAt: string;
  endsAt: string;
}

export function durationMinutes(span: TimeSpan): number {
  return (Date.parse(span.endsAt) - Date.parse(span.startsAt)) / MS_PER_MINUTE;
}

/** `minutes` past `start`, as a Date — how every default and span pill lands. */
export function minutesAfter(start: Date, minutes: number): Date {
  return new Date(start.getTime() + minutes * MS_PER_MINUTE);
}

/**
 * The events that touch `day`, earliest first.
 *
 * Touch, not start: an event running 23:30 to 00:30 belongs to both days, and a
 * grid that only asked about start times would drop it off the second one. The
 * boundary is half-open at the end, so an event finishing exactly at midnight
 * stays on the day it was held and does not open the next one.
 */
export function eventsOn(events: CalendarEventDto[], day: Date): CalendarEventDto[] {
  const from = startOfDay(day).getTime();
  const to = addDays(day, 1).getTime();

  return events
    .filter((event) => Date.parse(event.endsAt) > from && Date.parse(event.startsAt) < to)
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
}

/** Where a span sits down a day column, as fractions of that day. */
export function dayPlacement(span: TimeSpan, day: Date): { top: number; height: number } {
  const from = startOfDay(day).getTime();
  const to = addDays(day, 1).getTime();
  const length = to - from;

  // Clamped to the day, so the overnight case draws the part that belongs here
  // and the rest belongs to tomorrow's column.
  const start = Math.max(Date.parse(span.startsAt), from);
  const end = Math.min(Date.parse(span.endsAt), to);

  return { top: (start - from) / length, height: Math.max(0, end - start) / length };
}

/** An event and the column it takes when others want the same hour. */
export interface PlacedEvent {
  event: CalendarEventDto;
  /** Which sub-column this one sits in, from 0. */
  lane: number;
  /** How many sub-columns the whole overlapping run needs. */
  lanes: number;
}

/**
 * Side by side, for events that overlap.
 *
 * Every event in one run of overlaps reports the same `lanes`, not just its own
 * neighbours' count, which is the point of collecting the run before assigning
 * widths: three interviews where the first two overlap and the third only
 * overlaps the second must all be a third of the column wide, or the blocks step
 * in and out and stop reading as one crowded morning.
 *
 * Lanes rather than pixels: this says who is beside whom, and the grid decides
 * what that is worth in width.
 */
export function packLanes(events: CalendarEventDto[]): PlacedEvent[] {
  const sorted = [...events].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  const placed: PlacedEvent[] = [];

  // The run being built, the lane each of its members took, and when each lane
  // last became free.
  let run: { event: CalendarEventDto; lane: number }[] = [];
  let laneEnds: number[] = [];
  let runEnd = -Infinity;

  const closeRun = () => {
    const lanes = Math.max(...run.map((entry) => entry.lane)) + 1;
    for (const entry of run) placed.push({ ...entry, lanes });
    run = [];
    laneEnds = [];
  };

  for (const event of sorted) {
    const start = Date.parse(event.startsAt);
    const end = Date.parse(event.endsAt);

    // Nothing in the run reaches this event, so the run is finished and this one
    // starts a fresh set of lanes.
    if (run.length > 0 && start >= runEnd) closeRun();

    const free = laneEnds.findIndex((laneEnd) => laneEnd <= start);
    const lane = free === -1 ? laneEnds.length : free;
    laneEnds[lane] = end;
    run.push({ event, lane });
    runEnd = Math.max(runEnd, end);
  }

  if (run.length > 0) closeRun();
  return placed;
}

/** Whether the event has run its course. */
export function isOver(event: { endsAt: string }, now = Date.now()): boolean {
  return Date.parse(event.endsAt) <= now;
}
